import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { StripeError } from "@stripe/stripe-js"
import {
  actionAlert,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  Typography,
} from "@suraasa/placebo-ui"
import { useMediaQuery } from "@suraasa/placebo-ui-legacy"
import api from "api"
import StripeLogo from "assets/stripeLogo.webp"
import clsx from "clsx"
import { FocusTrap } from "focus-trap-react"
import { useGlobalContext } from "global/Context/context"
import { Xmark } from "iconoir-react"
import { ChevronDownIcon, ChevronUpIcon, UserIcon } from "lucide-react"
import React, { useEffect, useState } from "react"
import { mapErrorsV2 } from "utils/helpers"

type Props = {
  open: boolean
  handleClose: () => void
  itemName: string
  price: string
  logo?: () => JSX.Element
  logoSrc?: string
  redirectURL: string
  isUpdatePaymentMethod?: boolean
}

export default function StripeCheckoutForm({
  logo,
  logoSrc,
  open,
  handleClose,
  itemName,
  price,
  redirectURL,
  isUpdatePaymentMethod = false,
}: Props) {
  const stripe = useStripe()
  const elements = useElements()

  const { authInfo } = useGlobalContext()

  const [message, setMessage] = useState<{
    text: string
    type: "success" | "failure" | "info"
  } | null>(null)

  const [isLoading, setIsLoading] = useState(false)

  const isSmDown = useMediaQuery("only screen and (max-width : 640px)")

  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!stripe) {
      return
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    )

    if (!clientSecret) {
      return
    }

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }: any) => {
        if (!paymentIntent) return
        switch (paymentIntent.status) {
          case "succeeded":
            setMessage({ text: "Payment succeeded!", type: "success" })
            break
          case "processing":
            setMessage({ text: "Your payment is processing.", type: "info" })
            break
          case "requires_payment_method":
            setMessage({
              text: "Your payment was not successful, please try again.",
              type: "failure",
            })
            break
          default:
            setMessage({ text: "Something went wrong.", type: "failure" })
            break
        }
      })
  }, [stripe])

  const handleError = (error: StripeError) => {
    if (error.message) {
      setMessage({ text: error.message, type: "failure" })
    } else {
      setMessage({ text: "An unexpected error occurred.", type: "failure" })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setIsLoading(true)

    if (isUpdatePaymentMethod) {
      try {
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/account`,
            // If you want to handle next steps in this component instead of redirect:
            expand: ["payment_method"],
          },
          redirect: "if_required",
        })

        if (!result.error) {
          await api.subscriptions.updatePaymentMethod({
            data: {
              setup_indent_id: result.setupIntent.id,
              mandate_payment_method_id:
                result.setupIntent?.payment_method?.["id"],
            },
          })

          setIsLoading(false)

          await actionAlert.success({
            title: "Payment method updated successfully",
            duration: 5000,
            onClose: handleClose,
          })

          handleClose()
        } else {
          const error = result.error
          handleError(error)
        }
      } catch (error) {
        mapErrorsV2(error)
      } finally {
        setIsLoading(false)
      }

      return
    }

    // Handle regular payment confirmation
    try {
      const stripeResponse = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: redirectURL,
        },
      })

      const { error } = stripeResponse

      // This point will only be reached if there is an immediate error when
      // confirming the payment. Otherwise, your customer will be redirected to
      // your `return_url`. For some payment methods like iDEAL, your customer will
      // be redirected to an intermediate site first to authorize the payment, then
      // redirected to the `return_url`.
      handleError(error)
    } catch (error) {
      console.error("Payment confirmation error:", error)
      setMessage({ text: "An unexpected error occurred.", type: "failure" })

      setIsLoading(false)
    }
  }

  const Details = (
    <div
      className={clsx(
        "relative flex flex-col rounded-lg bg-primary-500 p-2 text-white",
        {
          "!rounded-r-none": !isSmDown,
          "h-full": !isSmDown,
        }
      )}
    >
      <div className="flex flex-row">
        {logoSrc && (
          <div
            className={clsx(
              "mr-1.5 flex max-h-10 max-w-10 items-center justify-center rounded-sm",
              { "max-h-5 max-w-5": isSmDown }
            )}
          >
            <img
              alt="purchase"
              src={logoSrc}
              className="rounded-md object-contain"
            />
          </div>
        )}
        {logo && logo()}

        <Typography variant="title4" className="self-center">
          {itemName}
        </Typography>
        {isSmDown && (
          <>
            {isAccordionOpen && (
              <ChevronUpIcon
                onClick={() => setIsAccordionOpen(false)}
                className="ml-2 size-5 text-white"
              />
            )}
            {!isAccordionOpen && (
              <ChevronDownIcon
                onClick={() => setIsAccordionOpen(true)}
                className="ml-2 size-5 text-white"
              />
            )}
          </>
        )}
      </div>

      {!isSmDown && !isUpdatePaymentMethod && (
        <div className="mt-2 flex flex-wrap justify-between gap-1 rounded-md bg-surface-100 p-2 md:flex-col">
          <Typography variant="smallBody" className="text-black-500">
            Price Summary
          </Typography>
          <Typography variant="strong" className="text-black-500">
            {price}
          </Typography>
        </div>
      )}
      {(isAccordionOpen || !isSmDown) && (
        <div className="mt-2 flex flex-row gap-1 rounded-md bg-surface-100 p-2">
          <UserIcon className="size-3 text-primary-500 " />
          <div className="flex flex-col gap-1">
            <Typography variant="smallBody" className="text-onSurface-900">
              Using as
            </Typography>
            <Typography
              variant="smallBody"
              className="break-all text-onSurface-600"
            >
              {authInfo?.user.email}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )

  const confirmationToCloseDialog = (
    <FocusTrap>
      <div
        className="absolute inset-0 z-50 flex items-end rounded-lg bg-black bg-opacity-[40%] duration-200 animate-in fade-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-title"
      >
        <div className="w-full rounded-t-lg bg-white p-4 duration-300 ease-out animate-in slide-in-from-bottom">
          <Typography
            variant={"title3"}
            className="text-center"
            id="confirmation-title"
          >
            Are you sure you want to exit?
          </Typography>
          <Typography variant={"smallBody"} className="mt-2 text-center">
            Your payment information will not be saved and you&apos;ll need to
            enter it again.
          </Typography>
          <div className="mt-4 flex flex-col gap-1.5">
            <Button
              onClick={() => setShowConfirmation(false)}
              variant={"outlined"}
              color={"black"}
              className="!border !py-1.25"
            >
              Continue to Payment
            </Button>
            <Button
              onClick={handleClose}
              variant={"filled"}
              color="black"
              className="!py-1.5"
            >
              Yes, exit
            </Button>
          </div>
        </div>
      </div>
    </FocusTrap>
  )

  const CloseButton = (
    <IconButton
      aria-label="close"
      color={"black"}
      onClick={() => setShowConfirmation(true)}
    >
      <Xmark />
    </IconButton>
  )

  const Form = (
    <form onSubmit={handleSubmit} className="relative rounded-lg bg-white p-2">
      <div className={clsx("flex justify-end", { hidden: isSmDown })}>
        {CloseButton}
      </div>
      {/* Show any error or success messages */}
      <div
        className={clsx("my-2", {
          "text-success-500": message?.type === "success",
          "text-critical-500": message?.type === "failure",
          "text-black-500": message?.type === "info",
        })}
      >
        {message && <Typography variant="title3">{message.text}</Typography>}
      </div>

      <PaymentElement />

      <div className="sticky bottom-0 mt-3">
        <Button
          color="black"
          className="w-full rounded-lg py-1.5"
          disabled={isLoading || !stripe || !elements}
          loading={isLoading}
          size="sm"
          type="submit"
          variant="filled"
          rounded
        >
          {isUpdatePaymentMethod ? "Update Payment Method" : `Pay ${price}`}{" "}
        </Button>
      </div>

      <div className="mb-2 mt-1 flex flex-row items-center justify-center gap-0.5">
        <Typography variant={"smallBody"} className="text-onSurface-600">
          Powered by
        </Typography>
        <img src={StripeLogo} alt="stripe" className="h-2 bg-white" />
      </div>
      {showConfirmation && !isSmDown && confirmationToCloseDialog}
    </form>
  )

  if (isSmDown) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          height={90}
          className="max-w-none rounded-t-2xl bg-white !p-0"
        >
          <SheetHeader hideClose>
            <span />
            {CloseButton}
          </SheetHeader>
          <SheetBody>
            {Details}
            {Form}
            {showConfirmation && confirmationToCloseDialog}
          </SheetBody>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open}>
      <DialogContent className="flex max-w-screen-md !flex-row">
        <div className="w-[45%]">{Details}</div>
        <div className="w-full rounded-r-lg bg-primary-500 p-2">{Form}</div>
      </DialogContent>
    </Dialog>
  )
}
